import { useState } from "react";
import { CalendarDays, Check, MessageSquareWarning } from "lucide-react";
import {
  useGetIdentity,
  useGetList,
  useGetOne,
  useLocaleState,
  useNotify,
  useRefresh,
  useTranslate,
} from "ra-core";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { formatDateOnly } from "../clients/clientModel";
import { PieceComments } from "../calendar/PieceComments";
import { StatusPill } from "../calendar/PieceDetail";
import { PieceVersions } from "../calendar/PieceVersions";
import { FORMAT_ICONS } from "../calendar/pieceModel";
import { getSupabaseClient } from "../providers/supabase/supabase";
import { useScrollIntoViewOnNarrow } from "../calendar/useScrollIntoViewOnNarrow";
import type { ContentPiece, ContentPieceVersion } from "../types";

export const PortalPieceDetail = ({ pieceId }: { pieceId: string | null }) => {
  const translate = useTranslate();
  if (!pieceId) {
    return (
      <aside className="rounded-[22px] bg-card p-6 text-sm text-muted-foreground">
        {translate("crm.portal.no_selection")}
      </aside>
    );
  }
  return <PortalPieceDetailContent key={pieceId} pieceId={pieceId} />;
};

const PortalPieceDetailContent = ({ pieceId }: { pieceId: string }) => {
  const translate = useTranslate();
  const [locale = "es"] = useLocaleState();
  const { identity } = useGetIdentity();
  const { data: piece } = useGetOne<ContentPiece>("content_pieces", {
    id: pieceId,
  });
  const { data: versions = [] } = useGetList<ContentPieceVersion>(
    "content_piece_versions",
    {
      filter: { piece_id: pieceId },
      sort: { field: "version_number", order: "DESC" },
      pagination: { page: 1, perPage: 100 },
    },
  );

  const detailRef = useScrollIntoViewOnNarrow<HTMLElement>(piece != null);

  if (!piece) return <aside className="rounded-[22px] bg-card p-6" />;
  const FormatIcon = FORMAT_ICONS[piece.format];

  return (
    <aside
      ref={detailRef}
      className="flex scroll-mt-4 flex-col gap-5 rounded-[22px] bg-card p-5"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-xl font-semibold leading-snug tracking-tight">
            {piece.title}
          </h2>
          <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <FormatIcon className="size-3.5" />
              {translate(`resources.content_pieces.formats.${piece.format}`)}
            </span>
            {piece.publish_date ? (
              <span className="flex items-center gap-1">
                <CalendarDays className="size-3.5" />
                {formatDateOnly(piece.publish_date, locale)}
              </span>
            ) : null}
          </div>
        </div>
        <StatusPill status={piece.status} />
      </div>

      {piece.status === "in_review" ? <ReviewActions piece={piece} /> : null}
      {piece.status === "in_review" ? (
        <div aria-hidden className="h-32 xl:hidden" />
      ) : null}

      <PieceVersions
        piece={piece}
        versions={versions}
        accountName={identity?.companyName}
        canAdd={false}
      />

      {piece.script ? (
        <TextBlock label={translate("resources.content_pieces.fields.script")}>
          {piece.script}
        </TextBlock>
      ) : null}
      {piece.caption && versions.length === 0 ? (
        <TextBlock label={translate("resources.content_pieces.fields.caption")}>
          {piece.caption}
        </TextBlock>
      ) : null}

      <PieceComments
        pieceId={piece.id}
        authorKind="client"
        authorName={identity?.fullName}
      />
    </aside>
  );
};

// Approve / request changes go through the review_content_piece RPC: the only
// write path a client has on a piece.
const ReviewActions = ({ piece }: { piece: ContentPiece }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [isRequesting, setIsRequesting] = useState(false);
  const [comment, setComment] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const review = async (decision: "approved" | "changes_requested") => {
    if (decision === "changes_requested" && !comment.trim()) {
      setError(translate("crm.portal.comment_required"));
      return;
    }
    setIsSaving(true);
    const { error: rpcError } = await getSupabaseClient().rpc(
      "review_content_piece",
      {
        target_piece_id: piece.id,
        decision,
        reviewer_comment: comment.trim() || null,
      },
    );
    setIsSaving(false);
    if (rpcError) {
      notify("ra.notification.http_error", { type: "error" });
      return;
    }
    notify(
      decision === "approved"
        ? "crm.portal.approved_toast"
        : "crm.portal.changes_toast",
      { type: "success" },
    );
    setIsRequesting(false);
    setComment("");
    refresh();
  };

  return (
    <div className="fixed inset-x-0 bottom-0 z-40 flex flex-col gap-3 border-t border-border bg-card/95 p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] shadow-[0_-8px_24px_rgba(0,0,0,0.35)] backdrop-blur xl:static xl:z-auto xl:rounded-2xl xl:border-0 xl:bg-primary/10 xl:pb-4 xl:shadow-none xl:backdrop-blur-none">
      <p className="text-sm text-primary">
        <span className="font-semibold xl:hidden">{piece.title} · </span>
        {translate("crm.portal.waiting_hint")}
      </p>
      {isRequesting ? (
        <>
          <Textarea
            rows={3}
            autoFocus
            value={comment}
            placeholder={translate("crm.portal.changes_placeholder")}
            onChange={(event) => {
              setComment(event.target.value);
              setError(null);
            }}
          />
          {error ? <p className="text-xs text-destructive">{error}</p> : null}
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => {
                setIsRequesting(false);
                setError(null);
              }}
            >
              {translate("ra.action.cancel")}
            </Button>
            <Button
              className="rounded-full"
              disabled={isSaving}
              onClick={() => review("changes_requested")}
            >
              {translate("crm.portal.send_changes")}
            </Button>
          </div>
        </>
      ) : (
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="flex-1 rounded-full"
            disabled={isSaving}
            onClick={() => setIsRequesting(true)}
          >
            <MessageSquareWarning className="size-4" />
            {translate("crm.portal.request_changes")}
          </Button>
          <Button
            className="flex-1 rounded-full"
            disabled={isSaving}
            onClick={() => review("approved")}
          >
            <Check className="size-4" />
            {translate("crm.portal.approve")}
          </Button>
        </div>
      )}
    </div>
  );
};

const TextBlock = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div>
    <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">
      {label}
    </div>
    <p className="whitespace-pre-line text-sm leading-relaxed text-foreground/85">
      {children}
    </p>
  </div>
);
